import { Controller } from "web-ui/controller"
import { computePosition, flip, shift, limitShift, offset, size, autoUpdate } from "@floating-ui/dom"
import { morphdomIgnore } from "web-ui/morphdom/ignore"
import * as Log from "web-ui/log"

const SUPPRESS_REOPEN_WINDOW = 100

// Connects to data-controller="help-button"
export default class extends Controller {
  static targets = ["button", "template"]

  static events = {
    "toggle@document": "handleToggle"
  }

  connect() {
    this.debug("Connect")
    this.lastCloseTime = 0
  }

  disconnect() {
    this.debug("Disconnect")
    this.removePopover()
  }

  toggle(event) {
    event.preventDefault()

    if (this.popoverElement) {
      this.popoverElement.hidePopover()
      return
    }

    // Light dismissal happens on pointerdown and fires the toggle event before
    // click — without this guard, clicking the button while the popover is open
    // would close (via light dismissal) and immediately re-open. - Aaron, Tue Apr 28 2026
    if (Date.now() - this.lastCloseTime < SUPPRESS_REOPEN_WINDOW) {
      return
    }

    this.open()
  }

  open() {
    morphdomIgnore(this.element, true)

    const fragment = this.templateTarget.content.cloneNode(true)
    this.popoverElement = fragment.firstElementChild
    document.body.appendChild(this.popoverElement)

    this.startIntersectionObserver()

    this.buttonTarget.setAttribute("aria-expanded", "true")
    this.popoverElement.showPopover()

    // Wait for the popover to render before computing position so flip() can
    // measure the popover's actual size. - Aaron, Tue Apr 28 2026
    requestAnimationFrame(() => {
      if (!this.popoverElement) return

      this.disableAutoUpdate = autoUpdate(
        this.buttonTarget,
        this.popoverElement,
        this.updatePosition
      )
    })
  }

  handleToggle(event) {
    if (event.target !== this.popoverElement) return

    if (event.newState === "closed") {
      this.lastCloseTime = Date.now()
      this.removePopover()
    }
  }

  removePopover() {
    morphdomIgnore(this.element, false)
    this.stopAutoUpdate()
    this.stopIntersectionObserver()

    this.buttonTarget.setAttribute("aria-expanded", "false")

    if (this.popoverElement) {
      const popover = this.popoverElement
      this.popoverElement = null

      // Wait for the close animation to finish before removing, otherwise the
      // element is gone before CSS transitions can play - Aaron, Tue Apr 28 2026
      const animations = popover.getAnimations()
      Promise.allSettled(animations.map((animation) => animation.finished))
        .then(() => popover.remove())
    }
  }

  stopAutoUpdate() {
    if (this.disableAutoUpdate) {
      this.disableAutoUpdate()
      this.disableAutoUpdate = null
    }
  }

  startIntersectionObserver() {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry.isIntersecting && this.popoverElement) {
          this.popoverElement.hidePopover()
        }
      },
      { threshold: 0 }
    )
    this.intersectionObserver.observe(this.buttonTarget)
  }

  stopIntersectionObserver() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
      this.intersectionObserver = null
    }
  }

  updatePosition = () => {
    if (!this.popoverElement) return Promise.resolve()

    return computePosition(this.buttonTarget, this.popoverElement, {
      placement: "top",
      strategy: "fixed",
      middleware: [
        offset(8),
        shift({ padding: 16, limiter: limitShift({ offset: 32 }) }),
        flip({ padding: 16 }),
        size({
          padding: 16,
          apply: ({ availableHeight, elements }) => {
            elements.floating.style.maxHeight = `${availableHeight}px`
          },
        }),
      ],
    }).then(({ x, y, placement }) => {
      if (!this.popoverElement) return

      if (placement === "top") {
        this.popoverElement.classList.add("top")
        this.popoverElement.classList.remove("bottom")
      } else {
        this.popoverElement.classList.add("bottom")
        this.popoverElement.classList.remove("top")
      }

      // Calculate arrow position relative to popover (center of button)
      const buttonRect = this.buttonTarget.getBoundingClientRect()
      const buttonCenterX = buttonRect.left + buttonRect.width / 2
      // Subtract 1px to account for border/transform rounding
      const arrowX = buttonCenterX - x - 1

      this.popoverElement.style.left = `${x}px`
      this.popoverElement.style.top = `${y}px`
      this.popoverElement.style.setProperty("--help-button-arrow-x", `${arrowX}px`)

      const scrollIndicator = this.popoverElement.querySelector("scroll-indicator")
      if (scrollIndicator && scrollIndicator.displayIndicators) {
        scrollIndicator.displayIndicators()
      }
    })
  }

  debug(message, data) {
    Log.debug("Help Button - " + message, data)
  }
}
